import { useEffect } from 'react'

interface PageMetaOptions {
  title?: string
  description?: string
  ogImage?: string
  ogUrl?: string
}

/**
 * Atualiza dinamicamente as metatags da página ativa (title, og:*, twitter:*).
 * Restaura os valores originais do index.html ao desmontar.
 * Protegido contra ambiente de pré-renderização (SSR) onde `document` não existe.
 */
export function usePageMeta({ title, description, ogImage, ogUrl }: PageMetaOptions) {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const defaults = {
      title: document.title,
      description: getMeta('name', 'description'),
      ogTitle: getMeta('property', 'og:title'),
      ogDescription: getMeta('property', 'og:description'),
      ogImage: getMeta('property', 'og:image'),
      ogUrl: getMeta('property', 'og:url'),
      twitterTitle: getMeta('name', 'twitter:title'),
      twitterDescription: getMeta('name', 'twitter:description'),
      twitterImage: getMeta('name', 'twitter:image'),
    }

    if (title) {
      document.title = title
      setMeta('property', 'og:title', title)
      setMeta('name', 'twitter:title', title)
    }
    if (description) {
      setMeta('name', 'description', description)
      setMeta('property', 'og:description', description)
      setMeta('name', 'twitter:description', description)
    }
    if (ogImage) {
      setMeta('property', 'og:image', ogImage)
      setMeta('name', 'twitter:image', ogImage)
    }
    if (ogUrl) {
      setMeta('property', 'og:url', ogUrl)
    }

    return () => {
      if (typeof document === 'undefined') return
      document.title = defaults.title
      setMeta('name', 'description', defaults.description)
      setMeta('property', 'og:title', defaults.ogTitle)
      setMeta('property', 'og:description', defaults.ogDescription)
      setMeta('property', 'og:image', defaults.ogImage)
      setMeta('property', 'og:url', defaults.ogUrl)
      setMeta('name', 'twitter:title', defaults.twitterTitle)
      setMeta('name', 'twitter:description', defaults.twitterDescription)
      setMeta('name', 'twitter:image', defaults.twitterImage)
    }
  }, [title, description, ogImage, ogUrl])
}

function getMeta(attr: string, value: string): string {
  if (typeof document === 'undefined') return ''
  return document.querySelector(`meta[${attr}="${value}"]`)?.getAttribute('content') ?? ''
}

function setMeta(attr: string, value: string, content: string) {
  if (typeof document === 'undefined') return
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${value}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, value)
    document.head.appendChild(el)
  }
  el.content = content
}
